import React, { useState, useEffect, useRef } from "react";
import { dataSlider } from "../../data/Slider/dataSlider";
import SlidersModal from "./SlidersModal";
import "./Slider.css";
import { FaHandPointUp } from "react-icons/fa";

// const BANNERS_API = "https://sigec.test/api/banners";

/* Convierte URL relativa a URL absoluta del servidor legacy */
const toAbsUrl = (imgUrl) => {
  if (!imgUrl) return "";
  if (imgUrl.startsWith("http")) return imgUrl;
  return `https://itetlax.org.mx/assets/img/imgC_I/${imgUrl}`;
};

/* dataSlider normalizado (URLs absolutas). Se calcula una sola vez fuera del componente */
const LOCAL_SLIDES = dataSlider.map((item) => ({
  ...item,
  imgUrl: toAbsUrl(item.imgUrl),
}));

const Slider = () => {
  /* Arranca con los datos locales inmediatamente (sin spinner ni delay) */
  const slides = LOCAL_SLIDES;
  // const [slides, setSlides] = useState(LOCAL_SLIDES);
  const [showModal, setShowModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const carouselRef = useRef(null);

  /* ── Obtiene banners del admin en background (Comentado temporalmente) ──
  useEffect(() => {
    const controller = new AbortController();

    fetch(BANNERS_API, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((apiData) => {
        if (!Array.isArray(apiData) || apiData.length === 0) return;

        // Combina: API primero (orden controlado desde el admin),
        // luego los locales que NO existen ya en la API (por imgUrl).
        // Esto evita duplicados y respeta el orden del admin.
        const apiUrls = new Set(apiData.map((b) => b.imgUrl));
        const localExtra = LOCAL_SLIDES.filter(
          (item) => !apiUrls.has(item.imgUrl)
        );

        setSlides([...apiData, ...localExtra]);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.warn("[Slider] API no disponible — mostrando banners locales.", err);
        }
      });

    return () => controller.abort();
  }, []);
  ────────────────────────────────────────────────────────────────────────── */

  /* ── Inicializa Bootstrap Carousel ────────────────────────────────────── */
  useEffect(() => {
    const el = carouselRef.current;
    if (!el || slides.length === 0) return;

    const timer = setTimeout(() => {
      if (!window.bootstrap?.Carousel) return;

      const prev = window.bootstrap.Carousel.getInstance(el);
      if (prev) prev.dispose();

      new window.bootstrap.Carousel(el, {
        interval: 4000,
        ride: "carousel",
        wrap: true,
      });
    }, 200);

    return () => clearTimeout(timer);
  }, [slides]);

  /*
   * activeIndex:
   *   - Datos de la API  → siempre 0  (el campo order controla el orden)
   *   - Datos locales    → el item con itemActive === "active", o 0 si ninguno
   *
   * Se usa Math.max(..., 0) para que findIndex=-1 resulte en 0.
   */
  const activeIndex =
    slides === LOCAL_SLIDES
      ? Math.max(
          slides.findIndex((s) => s.itemActive === "active"),
          0
        )
      : 0;

  /* ── Handlers ────────────────────────────────────────────────────────── */
  const handleImageClick = (item) => {
    if (item.modalimage || item.modaltext || item.modalzip) {
      setCurrentImage(item);
      setShowModal(true);
      document.body.style.overflow = "hidden";
    } else if (item.url) {
      window.open(item.url, "_blank");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentImage(null);
    document.body.style.overflow = "auto";
    setIsDownloading(false);
  };

  const downloadZipFile = () => {
    if (!currentImage?.zipFile) return;
    setIsDownloading(true);
    const zipUrl = `https://itetlax.org.mx/assets/img/ZIP/${currentImage.zipFile}`;
    setTimeout(() => {
      window.open(zipUrl, "_blank");
      setIsDownloading(false);
    }, 1500);
  };

  /* ── Render ──────────────────────────────────────────────────────────── */
  return (
    <div className="slider-container">
      {/*
       * data-bs-ride="carousel" se mantiene por compatibilidad,
       * pero el control real viene de nuestro useEffect con new Carousel().
       */}
      <div
        id="imageSlider"
        className="carousel slide"
        data-bs-ride="carousel"
        ref={carouselRef}
      >
        <div className="carousel-inner">
          {slides.map((item, index) => (
            /*
             * KEY = imgUrl (estable entre re-renders).
             * Antes era "slide-${id}-${index}" → cuando el array se reordenaba,
             * React desmontaba todos los items (keys diferentes) y Bootstrap
             * perdía su estado interno. Con imgUrl como key, React reutiliza
             * los nodos existentes y solo actualiza las props (clase active).
             */
            <div
              key={item.imgUrl || `slide-${index}`}
              className={`carousel-item ${index === activeIndex ? "active" : ""}`}
            >
              <div
                className="image-container"
                onClick={() => handleImageClick(item)}
                style={{
                  cursor:
                    item.url ||
                    item.modalimage ||
                    item.modaltext ||
                    item.modalzip
                      ? "pointer"
                      : "default",
                }}
              >
                <img
                  src={item.imgUrl}
                  className="d-block slider-image"
                  alt={item.alt}
                />
                {/* Botón pulse */}
                {item.buttonActive && (
                  <button className="pulse-button">
                    <FaHandPointUp size={24} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Flechas móviles */}
        <div className="mobile-nav-arrows">
          <div
            className="mobile-nav-arrow"
            onClick={() =>
              document.querySelector(".carousel-control-prev").click()
            }
          >
            &lt;
          </div>
          <div
            className="mobile-nav-arrow"
            onClick={() =>
              document.querySelector(".carousel-control-next").click()
            }
          >
            &gt;
          </div>
        </div>

        {/* Controles Bootstrap */}
        <button
          className="carousel-control-prev"
          type="button"
          data-bs-target="#imageSlider"
          data-bs-slide="prev"
        >
          <span
            className="carousel-control-prev-icon"
            aria-hidden="true"
          ></span>
          <span className="visually-hidden">Previous</span>
        </button>
        <button
          className="carousel-control-next"
          type="button"
          data-bs-target="#imageSlider"
          data-bs-slide="next"
        >
          <span
            className="carousel-control-next-icon"
            aria-hidden="true"
          ></span>
          <span className="visually-hidden">Next</span>
        </button>
      </div>

      <SlidersModal
        showModal={showModal}
        currentImage={currentImage}
        isDownloading={isDownloading}
        closeModal={closeModal}
        downloadZipFile={downloadZipFile}
      />
    </div>
  );
};

export default Slider;